import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

/**
 * TOTP enrolment. Required for super admins and platform staff; available to
 * everyone. Supabase handles the factor lifecycle, we handle the prompting.
 */
export function Security() {
  const [qr, setQr] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [enrolled, setEnrolled] = useState(false);

  useEffect(() => {
    void supabase.auth.mfa.listFactors().then(({ data }) => {
      setEnrolled(Boolean(data?.totp?.length));
    });
  }, []);

  async function start() {
    setStatus(null);
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp" });
    if (error || !data) {
      setStatus("Could not start enrolment. Try again.");
      return;
    }
    setFactorId(data.id);
    setQr(data.totp.qr_code);
  }

  async function confirm() {
    if (!factorId) return;
    const challenge = await supabase.auth.mfa.challenge({ factorId });
    if (challenge.error || !challenge.data) {
      setStatus("Could not verify that code.");
      return;
    }
    const { error } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.data.id,
      code,
    });
    if (error) {
      setStatus("That code was not accepted. Codes expire quickly — try the next one.");
      return;
    }
    setEnrolled(true);
    setQr(null);
    setStatus("Two-factor authentication is on.");
  }

  return (
    <>
      <header className="page-head">
        <h1>Security</h1>
      </header>
      <div className="card">
        <h2>Two-factor authentication</h2>
        {enrolled ? (
          <p className="muted">This account is protected by an authenticator app.</p>
        ) : (
          <>
            <p className="muted">
              Required for super admins. Scan the code with any authenticator app, then enter
              the six digits it shows.
            </p>
            {!qr && (
              <button className="btn" onClick={start}>
                Set up two-factor
              </button>
            )}
            {qr && (
              <div className="mfa">
                <img src={qr} alt="Two-factor setup QR code" width={180} height={180} />
                <div>
                  <label htmlFor="code">Six-digit code</label>
                  <input
                    id="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                  <button className="btn" onClick={confirm} disabled={code.length < 6}>
                    Confirm
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        {status && <p className="status">{status}</p>}
      </div>
    </>
  );
}
